import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../i18n/index.js';
import { guestSession } from '../guestSession.js';
import { GameIcon } from '../components/GameIcon.js';
import { RulesDialog } from '../components/RulesDialog.js';
import { BlackjackLobby, BlackjackTableList } from '../games/blackjack/BlackjackView.js';
import { EmojiPicker, insertAtCursor } from '../components/EmojiPicker.js';
import { SendButton } from '../components/SendButton.js';
import { formatDuration, useServerNow, type ArenaState } from '../arena/useArena.js';
import { chatMaxLength, checkChatMessage } from '../../shared/chatRules.js';
import type { ArenaSocket, Realtime } from '../realtime.js';
import {
  MULTI_GAME_INFO,
  MULTI_GAME_TYPES,
  TABLE_COUNTDOWNS,
  TABLE_DEFAULT_TURN_S,
  TABLE_TURN_TIMES_S,
  type ArenaResult,
  type MultiGameType,
  type MeUser,
  type PublicUser,
  type TableView,
} from '../../shared/protocol.js';

type Flash = { ok: boolean; text: string } | null;

function useEmit(socket: ArenaSocket | null) {
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<Flash>(null);
  const emit = async (fn: (s: ArenaSocket, done: (r: ArenaResult) => void) => void) => {
    if (!socket) return;
    setBusy(true);
    setFlash(null);
    const result = await new Promise<ArenaResult>((resolve) => fn(socket, resolve));
    setBusy(false);
    if (!result.ok) setFlash({ ok: false, text: t.arena.errors[result.error] });
  };
  return { busy, flash, setFlash, emit };
}

/**
 * Game tables for multiplayer games (decision 2026-09-27): open tables, opening a table and the
 * waiting room of the table the user sits at.
 */
export function TablesView(props: { user: MeUser; realtime: Realtime; arena: ArenaState }) {
  const { arena, realtime } = props;
  const table = arena.me?.table ?? null;
  const serverNow = useServerNow(realtime.clockOffset, 1000);

  useEffect(() => {
    // A notice about a closed table is shown once.
    if (!arena.tableClosed) return;
    const timer = setTimeout(arena.clearTableClosed, 6000);
    return () => clearTimeout(timer);
  }, [arena.tableClosed]);

  if (!realtime.socket || !arena.me) return <section class="card muted">{t.status.connecting}</section>;

  return (
    <div class="stack tables-page">
      {arena.tableClosed && (
        <p class="notice" role="status">
          {t.tables.closed[arena.tableClosed]}
        </p>
      )}
      {table ? (
        <TableRoom table={table} user={props.user} socket={realtime.socket} arena={arena} serverNow={serverNow} />
      ) : (
        <TableList user={props.user} socket={realtime.socket} arena={arena} serverNow={serverNow} />
      )}
    </div>
  );
}

function TableList(props: { user: MeUser; socket: ArenaSocket; arena: ArenaState; serverNow: number }) {
  const { arena, serverNow } = props;
  const [gameType, setGameType] = useState<MultiGameType>('dice');
  const [maxPlayers, setMaxPlayers] = useState(MULTI_GAME_INFO.dice.defaultMaxPlayers);
  const [countdownMin, setCountdownMin] = useState(0);
  const [turnTimeS, setTurnTimeS] = useState<number>(TABLE_DEFAULT_TURN_S.dice);
  const [showRules, setShowRules] = useState(false);
  // Blackjack is chosen like the other games; its own choices open below (user request 2026-10-01).
  const [blackjack, setBlackjack] = useState(false);
  const { busy, flash, emit } = useEmit(props.socket);
  const info = MULTI_GAME_INFO[gameType];
  const phase = arena.closure?.phase;
  const blocked = props.user.role !== 'admin' && (phase === 'draining' || phase === 'closed');
  const inGame = Boolean(arena.me?.activeGame || arena.me?.multiGame);
  // Open tables of the game chosen above only; a started game is no longer listed (user request 2026-10-01).
  const shown = arena.tables.filter((tb) => tb.gameType === gameType);

  const create = (e: Event) => {
    e.preventDefault();
    void emit((s, done) => s.emit('table:create', { gameType, maxPlayers, countdownMin, turnTimeS }, done));
  };

  return (
    <>
      <section class="card">
        <h2>{t.tables.title}</h2>
        <p class="muted">{t.tables.intro}</p>
        <form class="table-create" onSubmit={create}>
          <div class="game-grid table-games">
            {MULTI_GAME_TYPES.map((g) => (
              <button
                type="button"
                class={`game-card ${!blackjack && gameType === g ? 'selected' : ''}`}
                onClick={() => {
                  setBlackjack(false);
                  setGameType(g);
                  setMaxPlayers(MULTI_GAME_INFO[g].defaultMaxPlayers);
                  setTurnTimeS(TABLE_DEFAULT_TURN_S[g]);
                }}
                aria-pressed={gameType === g}
              >
                <GameIcon game={g} />
                <span class="game-name">{t.games[g].name}</span>
                <span class="game-desc">{t.games[g].description}</span>
              </button>
            ))}
            {/* Blackjack is played with AreCoin: not for guests (user decision 2026-10-01). */}
            {props.user.guestUntil === null && (
              <button type="button" class={`game-card ${blackjack ? 'selected' : ''}`} onClick={() => setBlackjack(true)} aria-pressed={blackjack}>
              <BlackjackIcon />
              <span class="game-name">Blackjack</span>
              <span class="game-desc">Tietokonejakajaa vastaan yksin tai porukalla – panokset AreCoineina.</span>
              </button>
            )}
            {/* The poker machine (2026-10-03): AreCoin as well, its own page. */}
            {props.user.guestUntil === null && (
              <button type="button" class="game-card" onClick={() => (location.hash = '#/pokeri')}>
                <PokerIcon />
                <span class="game-name">{t.poker.tableCard}</span>
                <span class="game-desc">{t.poker.tableCardDesc}</span>
              </button>
            )}
          </div>
          {blackjack ? (
            <BlackjackLobby embedded />
          ) : (
            <>
          <div class="grid-2">
            <label>
              {t.tables.maxPlayers}: <strong class="range-value">{maxPlayers}</strong>
              <input
                type="range"
                min={2}
                max={info.maxPlayers}
                value={maxPlayers}
                onInput={(e) => setMaxPlayers(Number(e.currentTarget.value))}
              />
            </label>
            <label>
              {t.tables.countdown}
              <select value={countdownMin} onChange={(e) => setCountdownMin(Number(e.currentTarget.value))}>
                {TABLE_COUNTDOWNS.map((m) => (
                  <option value={m}>{m === 0 ? t.tables.countdownNone : t.tables.countdownMin(m)}</option>
                ))}
              </select>
            </label>
            <label>
              {t.tables.turnTime}
              <select value={turnTimeS} onChange={(e) => setTurnTimeS(Number(e.currentTarget.value))}>
                {TABLE_TURN_TIMES_S[gameType].map((s) => (
                  <option value={s}>{t.tables.turnTimeOption(s)}</option>
                ))}
              </select>
            </label>
          </div>
          <div class="table-create-actions">
            <button class="btn btn-primary" type="submit" disabled={busy || blocked || inGame}>
              {t.tables.open}
            </button>
            <button type="button" class="btn btn-small" onClick={() => setShowRules(true)}>
              📖 {t.rules.buttonFor(t.games[gameType].name)}
            </button>
          </div>
          {blocked && <p class="muted">{t.arena.errors.arena_closing}</p>}
            </>
          )}
          {flash && (
            <p class={flash.ok ? 'success' : 'error'} role="status">
              {flash.text}
            </p>
          )}
        </form>
      </section>

      <section class="card">
        <h2>{t.tables.openTables}{blackjack ? ' – Blackjack' : ` – ${t.games[gameType].name}`}</h2>
        {blackjack ? (
          <BlackjackTableList />
        ) : shown.length === 0 ? (
          <p class="muted empty">{t.tables.none}</p>
        ) : (
          <ul class="table-list">
            {shown.map((tb) => {
              const full = tb.seats.length >= tb.maxPlayers;
              return (
                <li class="table-row">
                  <GameIcon game={tb.gameType} size={34} />
                  <div class="table-main">
                    <div class="table-name">
                      {t.games[tb.gameType].name} <span class="table-seats">{t.tables.seats(tb.seats.length, tb.maxPlayers)}</span>
                    </div>
                    <div class="muted table-sub">
                      {t.tables.hostedBy(tb.host.displayName)} · {t.tables.perTurn(tb.turnTimeMs / 1000)} ·{' '}
                      {tb.startsAt
                        ? t.tables.startsIn(formatDuration(tb.startsAt - serverNow))
                        : t.tables.expiresIn(formatDuration(tb.expiresAt - serverNow))}
                    </div>
                    <div class="table-players">{tb.seats.map((s) => s.displayName).join(', ')}</div>
                  </div>
                  <button
                    class="btn btn-primary btn-small"
                    disabled={busy || full || blocked || inGame}
                    onClick={() => void emit((s, done) => s.emit('table:join', { id: tb.id }, done))}
                  >
                    {full ? t.tables.full : t.tables.join}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
      {showRules && <RulesDialog game={gameType} onClose={() => setShowRules(false)} />}
    </>
  );
}

function TableRoom(props: { table: TableView; user: PublicUser; socket: ArenaSocket; arena: ArenaState; serverNow: number }) {
  const { table, user, socket, serverNow } = props;
  const isHost = table.host.id === user.id;
  const { busy, flash, emit } = useEmit(socket);
  const [showRules, setShowRules] = useState(false);
  const free = Math.max(0, table.maxPlayers - table.seats.length);

  return (
    <div class="table-room">
      <section class="card">
        <div class="section-head">
          <h2>
            <GameIcon game={table.gameType} size={30} /> {t.tables.room(t.games[table.gameType].name)}
          </h2>
          <button class="btn btn-small" onClick={() => setShowRules(true)}>
            📖 {t.rules.buttonShort}
          </button>
        </div>
        <p class="muted">
          {table.startsAt
            ? t.tables.autoStart(formatDuration(table.startsAt - serverNow))
            : isHost
              ? t.tables.fullStarts
              : t.tables.waitingHost(table.host.displayName)}{' '}
          {t.tables.expiresNote(formatDuration(table.expiresAt - serverNow))} {t.tables.turnTimeNote(table.turnTimeMs / 1000)}
        </p>

        <ul class="seat-list">
          {table.seats.map((s) => (
            <li class={`seat ${s.id === user.id ? 'mine' : ''}`}>
              <span class={`seat-avatar ${s.isAi ? 'ai' : ''}`} aria-hidden="true">
                {s.isAi ? '🤖' : s.displayName.slice(0, 1).toUpperCase()}
              </span>
              <span class="seat-name">
                {s.displayName}
                {s.id === table.host.id && <span class="seat-tag">{t.tables.host}</span>}
                {s.isAi && <span class="seat-tag">{t.tables.computer}</span>}
              </span>
              {isHost && s.id !== user.id && (
                <button
                  class="btn btn-ghost btn-small"
                  disabled={busy}
                  onClick={() => void emit((sk, done) => sk.emit('table:remove', { userId: s.id }, done))}
                >
                  {t.tables.remove}
                </button>
              )}
            </li>
          ))}
          {Array.from({ length: free }, () => (
            <li class="seat empty">
              <span class="seat-avatar" aria-hidden="true">
                ·
              </span>
              <span class="seat-name muted">{t.tables.emptySeat}</span>
            </li>
          ))}
        </ul>

        <div class="table-actions">
          {isHost && (
            <>
              <button class="btn btn-primary" disabled={busy} onClick={() => void emit((s, done) => s.emit('table:start', done))}>
                {table.seats.length === 1 ? t.tables.startAlone : t.tables.start}
              </button>
              <button class="btn" disabled={busy || free === 0} onClick={() => void emit((s, done) => s.emit('table:addAi', done))}>
                {t.tables.addComputer}
              </button>
            </>
          )}
          <button class="btn btn-ghost" disabled={busy} onClick={() => void emit((s, done) => s.emit('table:leave', done))}>
            {t.tables.leave}
          </button>
        </div>
        {isHost && table.seats.length === 1 && <p class="muted small-note">{t.tables.soloNote}</p>}
        {flash && (
          <p class={flash.ok ? 'success' : 'error'} role="status">
            {flash.text}
          </p>
        )}
      </section>
      <TableChat socket={socket} user={user} messages={props.arena.tableChat} />
      {showRules && <RulesDialog game={table.gameType} onClose={() => setShowRules(false)} />}
    </div>
  );
}

function TableChat(props: { socket: ArenaSocket; user: PublicUser; messages: ArenaState['tableChat'] }) {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const maxLength = chatMaxLength(props.user.role);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [props.messages.length]);

  const send = (e: Event) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    const rule = checkChatMessage(body, props.user.role);
    if (rule) return setError(t.arena.errors[rule]);
    setError(null);
    props.socket.emit('table:chat', { text: body }, (r: ArenaResult) => {
      if (r.ok) setText('');
      else setError(t.arena.errors[r.error]);
    });
  };

  return (
    <section class="card lobby-chat table-chat">
      <h2>{t.tables.chat}</h2>
      <div class="lobby-messages" ref={listRef}>
        {props.messages.length === 0 && <p class="muted">{t.lobbyChat.empty}</p>}
        {props.messages.map((m) => (
          <div class={`chat-line ${m.userId === props.user.id ? 'mine' : ''}`} key={m.id}>
            <span class="chat-time">{new Date(m.sentAt).toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' })}</span>{' '}
            <span class={`chat-nick role-${m.role ?? 'player'}`}>{m.displayName}</span>: <span class="chat-text">{m.text}</span>
          </div>
        ))}
      </div>
      {error && <p class="error">{error}</p>}
      {guestSession.guest ? (
        <p class="chat-readonly">{t.guest.readOnly}</p>
      ) : (
        <form class="chat-form" onSubmit={send}>
          <div class="chat-input-row">
            <input
              ref={inputRef}
              value={text}
              onInput={(e) => setText(e.currentTarget.value)}
              placeholder={t.tables.chatPlaceholder}
              maxLength={maxLength}
              aria-label={t.tables.chatPlaceholder}
            />
            <SendButton label={t.lobbyChat.send} disabled={!text.trim()} />
          </div>
          <div class="chat-tools">
            <EmojiPicker onPick={(emoji) => setText(insertAtCursor(inputRef.current, text, emoji))} />
            <span class="char-count" aria-hidden="true">
              {text.trim().length}/{maxLength}
            </span>
          </div>
        </form>
      )}
    </section>
  );
}

/** Blackjack's card in the game grid (2026-10-01): an ace and a king on green felt. */
function BlackjackIcon(props: { size?: number }) {
  const size = props.size ?? 40;
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" class="game-icon">
      <rect x="2" y="2" width="44" height="44" rx="12" fill="#0f5132" opacity="0.55" />
      <g transform="rotate(-12 18 26)">
        <rect x="8" y="12" width="20" height="28" rx="3" fill="#fff" stroke="#c8a24a" stroke-width="1" />
        <text x="11.5" y="22" font-size="9" font-weight="800" fill="#111">A</text>
        <text x="14" y="34" font-size="11" fill="#111">♠</text>
      </g>
      <g transform="rotate(10 30 26)">
        <rect x="21" y="10" width="20" height="28" rx="3" fill="#fff" stroke="#c8a24a" stroke-width="1" />
        <text x="24" y="20" font-size="9" font-weight="800" fill="#c81e1e">K</text>
        <text x="27" y="32" font-size="11" fill="#c81e1e">♥</text>
      </g>
    </svg>
  );
}

/** The poker machine's card in the game grid (2026-10-03): a joker over a fan of cards on a machine's blue. */
function PokerIcon(props: { size?: number }) {
  const size = props.size ?? 40;
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" class="game-icon">
      <rect x="2" y="2" width="44" height="44" rx="12" fill="#1e3a8a" opacity="0.6" />
      <rect x="7" y="13" width="15" height="22" rx="2.5" fill="#fff" stroke="#facc15" stroke-width="1" transform="rotate(-14 14 24)" />
      <rect x="25" y="13" width="15" height="22" rx="2.5" fill="#fff" stroke="#facc15" stroke-width="1" transform="rotate(14 32 24)" />
      <rect x="16" y="9" width="16" height="24" rx="2.5" fill="#fef3c7" stroke="#facc15" stroke-width="1.2" />
      <text x="24" y="25" font-size="12" text-anchor="middle" fill="#dc2626">★</text>
      <rect x="10" y="37" width="28" height="5" rx="2" fill="#facc15" />
    </svg>
  );
}
